import React, { useState } from 'react'
import { History, Search, Filter, Download, Clock, User, CheckCircle, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DatePickerWithRange } from '@/components/ui/date-range-picker'
import { useAlerts } from '@/contexts/AlertContext'
import { cn } from '@/lib/utils'

interface AlertFilters {
  search: string
  severity: string
  type: string
  status: string
  dateRange: {
    from: Date | null
    to: Date | null
  }
}

export const AlertHistory: React.FC = () => {
  const { alertHistory } = useAlerts()
  const [filters, setFilters] = useState<AlertFilters>({
    search: '',
    severity: 'all',
    type: 'all',
    status: 'all',
    dateRange: {
      from: null,
      to: null
    }
  })

  // Calculate statistics
  const stats = {
    total: alertHistory.length,
    today: alertHistory.filter(alert => {
      const today = new Date().toDateString()
      return alert.timestamp.toDateString() === today
    }).length,
    critical: alertHistory.filter(alert => alert.severity === 'critical').length,
    acknowledged: alertHistory.filter(alert => alert.acknowledged).length,
    avgResponseTime: 1.3 // Mock average response time in minutes
  }

  // Filter alerts
  const filteredAlerts = alertHistory.filter(alert => {
    if (filters.search && !alert.title.toLowerCase().includes(filters.search.toLowerCase()) && 
        !alert.message.toLowerCase().includes(filters.search.toLowerCase())) {
      return false
    }
    if (filters.severity !== 'all' && alert.severity !== filters.severity) {
      return false
    }
    if (filters.type !== 'all' && alert.type !== filters.type) {
      return false
    }
    if (filters.status !== 'all') {
      if (filters.status === 'acknowledged' && !alert.acknowledged) return false
      if (filters.status === 'unacknowledged' && alert.acknowledged) return false
    }
    if (filters.dateRange.from && alert.timestamp < filters.dateRange.from) {
      return false
    }
    if (filters.dateRange.to && alert.timestamp > filters.dateRange.to) {
      return false
    }
    return true
  })

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-risk-critical text-white'
      case 'high': return 'bg-risk-high text-white'
      case 'medium': return 'bg-risk-medium text-white'
      case 'low': return 'bg-risk-low text-white'
      default: return 'bg-muted text-muted-foreground'
    }
  }

  const getAlertIcon = (type: string) => {
    const icons = {
      sequential: '📱',
      velocity: '⚡',
      datacenter: '🖥️',
      domain: '📧',
      zip: '📍',
      pattern: '🔍'
    }
    return icons[type as keyof typeof icons] || '🚨'
  }

  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const calculateResponseTime = (alert: any) => {
    if (!alert.acknowledged || !alert.acknowledgedAt) return null
    const diff = alert.acknowledgedAt.getTime() - alert.timestamp.getTime()
    return Math.round(diff / 60000) // Convert to minutes
  }

  const exportAlerts = () => {
    const csvContent = [
      'Timestamp,Type,Severity,Title,Message,Affected Leads,Acknowledged,Response Time (min)',
      ...filteredAlerts.map(alert => {
        const responseTime = calculateResponseTime(alert)
        return [
          alert.timestamp.toISOString(),
          alert.type,
          alert.severity,
          `"${alert.title}"`,
          `"${alert.message}"`,
          alert.affectedLeads,
          alert.acknowledged ? 'Yes' : 'No',
          responseTime || 'N/A'
        ].join(',')
      })
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `alert-history-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History className="h-6 w-6 text-foreground" />
          <h2 className="text-2xl font-bold text-foreground">Alert History</h2>
        </div>
        <Button onClick={exportAlerts} variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-foreground">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total Alerts</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-foreground">{stats.today}</div>
            <div className="text-sm text-muted-foreground">Alerts Today</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-risk-critical">{stats.critical}</div>
            <div className="text-sm text-muted-foreground">Critical</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-status-approved">{stats.acknowledged}</div>
            <div className="text-sm text-muted-foreground">Acknowledged</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-foreground">{stats.avgResponseTime}m</div>
            <div className="text-sm text-muted-foreground">Avg Response</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search alerts..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-9"
              />
            </div>
            
            <Select
              value={filters.severity}
              onValueChange={(value) => setFilters(prev => ({ ...prev, severity: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.type}
              onValueChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="sequential">Sequential Phone</SelectItem>
                <SelectItem value="velocity">Velocity Attack</SelectItem>
                <SelectItem value="datacenter">Data Center IP</SelectItem>
                <SelectItem value="domain">Suspicious Domain</SelectItem>
                <SelectItem value="zip">Zip Concentration</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.status}
              onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="unacknowledged">Unacknowledged</SelectItem>
              </SelectContent>
            </Select>

            <DatePickerWithRange
              onDateChange={(range) => setFilters(prev => ({ ...prev, dateRange: range }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Alert List */}
      <Card>
        <CardHeader>
          <CardTitle>
            Alert History ({filteredAlerts.length} of {alertHistory.length})
          </CardTitle>
          <CardDescription>
            Complete history of fraud detection alerts and responses
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredAlerts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium mb-2">No alerts found</p>
                <p className="text-sm">Try adjusting your filters to see more results</p>
              </div>
            ) : (
              filteredAlerts.map((alert) => {
                const responseTime = calculateResponseTime(alert)
                return (
                  <div
                    key={alert.id}
                    className={cn(
                      "flex items-start gap-4 p-4 rounded-lg border transition-all duration-200",
                      alert.acknowledged ? "bg-muted/30 opacity-75" : "bg-background hover:bg-muted/20"
                    )}
                  >
                    <div className="text-2xl flex-shrink-0">
                      {getAlertIcon(alert.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground mb-1">
                            {alert.title}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {alert.message}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge className={getSeverityColor(alert.severity)} variant="secondary">
                            {alert.severity.toUpperCase()}
                          </Badge>
                          {alert.acknowledged && (
                            <Badge variant="outline" className="gap-1">
                              <CheckCircle className="h-3 w-3" />
                              Acked
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{formatDateTime(alert.timestamp)}</span>
                          </div>
                          <div>
                            {alert.affectedLeads} leads affected
                          </div>
                          {alert.acknowledgedBy && (
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              <span>Acked by {alert.acknowledgedBy}</span>
                            </div>
                          )}
                        </div>
                        
                        {responseTime && (
                          <div className="text-xs font-medium">
                            Response: {responseTime}m
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}